import React, { useState } from 'react';
import {
  Box, Button, Grid, IconButton, Popover, alpha, useTheme,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const MONTH_ABBREVS = ['Jan','Feb','Mar','Apr','May','Jun',
                       'Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * Format a period object into a human-readable label.
 *   { year: 2026, month: 9, view: 'month' } → "September 2026"
 *   { year: 2026, month: null, view: 'year' }  → "2026"
 */
export function formatPeriodLabel(period) {
  if (!period || period.view === 'year') {
    return String(period?.year ?? new Date().getFullYear());
  }
  return new Date(period.year, period.month - 1, 1)
    .toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

/** Returns the current month/year as the default period value. */
export function getDefaultPeriod() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, view: 'month' };
}

/**
 * A button that opens a popover month/year picker.
 *
 * Props:
 *   value    – { year: number, month: number|null, view: 'month'|'year' }
 *   onChange – (newPeriod) => void
 */
export default function MonthYearPicker({ value, onChange }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [navYear, setNavYear] = useState(value.year);
  const open = Boolean(anchorEl);

  const now = new Date();
  const maxYear = now.getFullYear();
  const maxMonth = now.getMonth() + 1; // 1-indexed

  const handleOpen = (e) => {
    setNavYear(value.year);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const selectMonth = (monthIdx) => {
    onChange({ year: navYear, month: monthIdx + 1, view: 'month' });
    handleClose();
  };

  const selectYear = () => {
    onChange({ year: navYear, month: null, view: 'year' });
    handleClose();
  };

  const goToNow = () => {
    onChange(getDefaultPeriod());
    handleClose();
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<CalendarTodayIcon />}
        endIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: '1rem !important',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        size="small"
        onClick={handleOpen}
      >
        {formatPeriodLabel(value)}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 6,
          sx: { p: 2, borderRadius: 2, width: 272, mt: 0.5 },
        }}
      >
        {/* ── Year row ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <IconButton size="small" onClick={() => setNavYear(y => y - 1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Button
            variant={value.view === 'year' && value.year === navYear ? 'contained' : 'text'}
            size="small"
            onClick={selectYear}
            title="Click to view data for the full year"
            sx={{ fontWeight: 700, fontSize: '1rem', minWidth: 80 }}
          >
            {navYear}
          </Button>
          <IconButton
            size="small"
            onClick={() => setNavYear(y => y + 1)}
            disabled={navYear >= maxYear}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Month grid ── */}
        <Grid container spacing={0.75}>
          {MONTH_ABBREVS.map((m, i) => {
            const isSelected =
              value.view === 'month' &&
              value.year === navYear &&
              value.month === i + 1;
            const isFuture = navYear === maxYear && (i + 1) > maxMonth;
            return (
              <Grid item xs={3} key={m}>
                <Button
                  fullWidth
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  disabled={isFuture}
                  onClick={() => selectMonth(i)}
                  sx={{
                    borderRadius: 1.5,
                    py: 0.75,
                    minWidth: 0,
                    fontSize: '0.78rem',
                    fontWeight: isSelected ? 700 : 400,
                    ...(!isSelected && {
                      borderColor: alpha(theme.palette.divider, 1),
                      color: 'text.secondary',
                      '&:hover:not(:disabled)': {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }),
                  }}
                >
                  {m}
                </Button>
              </Grid>
            );
          })}
        </Grid>

        {/* ── Quick link ── */}
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={goToNow}
            sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
          >
            Jump to current month
          </Button>
        </Box>
      </Popover>
    </>
  );
}
